import axios from "axios";
import { clearAuth } from "../authSlice";
import { store } from "../../../app/store";

const LOCAL_STORAGE_BACKUP_KEY = "__ams_localStorageBackup__";

type LocalStorageBackup = {
  userId: string | null;
  createdAt: number;
  data: Record<string, string>;
};

const safeJsonParse = <T,>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const getAuthUserIdFromStorage = (): string | null => {
  const authData = safeJsonParse<{ userId?: string | null; cognitoId?: string | null }>(
    localStorage.getItem("authData")
  );
  return authData?.userId ?? authData?.cognitoId ?? null;
};

const snapshotLocalStorage = (): Record<string, string> => {
  const snapshot: Record<string, string> = {};
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (!key) continue;
    if (key === LOCAL_STORAGE_BACKUP_KEY) continue;
    const value = localStorage.getItem(key);
    if (value !== null) snapshot[key] = value;
  }
  return snapshot;
};

export const saveLocalStorageBackupForCurrentUser = (): void => {
  try {
    const userId = getAuthUserIdFromStorage();
    const backup: LocalStorageBackup = {
      userId,
      createdAt: Date.now(),
      data: snapshotLocalStorage(),
    };
    localStorage.setItem(LOCAL_STORAGE_BACKUP_KEY, JSON.stringify(backup));
  } catch (error) {
    console.error("Failed to save localStorage backup:", error);
  }
};

export const restoreLocalStorageBackupIfSameUser = (userId: string | null | undefined): void => {
  if (!userId) return;

  try {
    const backup = safeJsonParse<LocalStorageBackup>(localStorage.getItem(LOCAL_STORAGE_BACKUP_KEY));
    if (!backup?.userId) return;
    if (backup.userId !== userId) return;

    for (const [key, value] of Object.entries(backup.data ?? {})) {
      if (key === "authData") continue;
      localStorage.setItem(key, value);
    }
  } catch (error) {
    console.error("Failed to restore localStorage backup:", error);
  }
};

const clearLocalStoragePreservingBackup = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (!key) continue;
      if (key === LOCAL_STORAGE_BACKUP_KEY) continue;
      keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error("Failed to clear localStorage:", error);
  }
};

const clientId = process.env.REACT_APP_CLIENT_ID || '';
const redirectUri = process.env.REACT_APP_REDIRECT_URI || '';
const tokenUrl = process.env.REACT_APP_TOKEN_URL || '';
const refreshTokenUrl = process.env.REACT_APP_REFRESH_TOKEN_URL || '';

export const exchangeCodeForToken = async (code: string) => {
  const params = new URLSearchParams();
  params.append("grant_type", "authorization_code");
  params.append("client_id", clientId);
  params.append("code", code);
  params.append("redirect_uri", redirectUri);
  try {
    const response = await axios.post(tokenUrl, params, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching token:", error);
    return null;
  }
};

export const refreshToken = async (): Promise<string | null> => {
  const storedAuthDataRaw = localStorage.getItem("authData");
  if (!storedAuthDataRaw) return null;

  const storedAuthData = JSON.parse(storedAuthDataRaw);
  const { refreshToken, cognitoId } = storedAuthData;

  if (!refreshToken || !cognitoId) {
    console.error("No refresh token or cognitoId found.");
    return null;
  }

  try {
    const response = await fetch(refreshTokenUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        refreshToken,
        username: cognitoId,
      }),
    });

    if (!response.ok) throw new Error("Failed to refresh token");

    const data = await response.json();

    const updatedAuthData = {
      ...storedAuthData,
      accessToken: data.accessToken,
      expiresIn: Date.now() + data.expiresIn * 1000,
    };

    localStorage.setItem("authData", JSON.stringify(updatedAuthData));
    return data.accessToken;
  } catch (error) {
    console.error("Token refresh error:", error);
    return null;
  }
};


export const getAccessToken = () => {
  const authData = JSON.parse(localStorage.getItem("authData") || '{}');
  return authData?.accessToken || null;
};
export const isAuthenticated = async (): Promise<boolean> => {
  const authData = JSON.parse(localStorage.getItem("authData") || '{}');
  const token = authData?.accessToken;
  const expiresAt = authData?.expiresIn;
  if (!token || !expiresAt) return false;
  const now = Date.now();
  if (now >= Number(expiresAt)) {
    const newToken = await refreshToken();
    if (!newToken) return false;
  }
  return true;
};
export const logOut = () => {
  saveLocalStorageBackupForCurrentUser();
  store.dispatch(clearAuth());
  clearLocalStoragePreservingBackup();
  window.location.href = "/";
}
